import React, { useEffect, useState } from 'react';
import {
  alpha,
  Avatar,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close, LocalShipping } from '@mui/icons-material';
import type { InventoryLead } from '@otw/models/leads/inventoryLead';
import { InventoryLeadForm } from '../forms';

interface InventoryLeadFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** When set, opens the form in edit mode for this lead. */
  selectedLead?: InventoryLead | null;
  /** Prefills the contact search when opened from a contact's CRM page. */
  initialContactId?: string | null;
  /** Prefills the company search when opened from a company's CRM page. */
  initialCompanyId?: string | null;
  /** Fires after a successful create, before the dialog closes. */
  onLeadCreated?: (lead: InventoryLead) => void | Promise<void>;
  /** Fires after a successful update, before the dialog closes. */
  onLeadUpdated?: (lead: InventoryLead) => void | Promise<void>;
}

const InventoryLeadFormDialog: React.FC<InventoryLeadFormDialogProps> = ({
  open,
  onClose,
  selectedLead = null,
  initialContactId = null,
  initialCompanyId = null,
  onLeadCreated,
  onLeadUpdated,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const accent = theme.palette.primary.main;
  const isEditing = Boolean(selectedLead);

  // Force a fresh form each time the dialog opens (or the edited lead changes).
  const [mountKey, setMountKey] = useState(0);
  useEffect(() => {
    if (open) setMountKey((k) => k + 1);
  }, [open, selectedLead?.id]);

  const handleCreated = async (lead: InventoryLead) => {
    await onLeadCreated?.(lead);
    onClose();
  };

  const handleUpdated = async (lead: InventoryLead) => {
    await onLeadUpdated?.(lead);
    onClose();
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      fullScreen={isSmallScreen}
      disableEscapeKeyDown
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') onClose();
      }}
      PaperProps={{
        sx: {
          maxHeight: { xs: '100vh', sm: '92vh' },
          height: 'auto',
          borderRadius: { xs: 0, sm: 3 },
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          p: { xs: 2, md: 2.5 },
          background: `linear-gradient(135deg, ${alpha(accent, 0.12)} 0%, ${alpha(accent, 0.04)} 100%)`,
          borderBottom: `1px solid ${alpha(accent, 0.18)}`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar
            variant="rounded"
            sx={{
              bgcolor: alpha(accent, 0.18),
              color: accent,
              width: 44,
              height: 44,
            }}
          >
            <LocalShipping />
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="overline"
              sx={{ color: accent, letterSpacing: 1.2, fontWeight: 800, fontSize: '1rem' }}
            >
              {isEditing ? 'Edit inventory lead' : 'New inventory lead'}
            </Typography>
            <Typography variant="caption" color="text.primary" sx={{ display: 'block', lineHeight: 1.4 }}>
              {isEditing
                ? 'Update contact, company, or truck details for this purchasing lead.'
                : 'Track a truck a contact or company wants to sell us.'}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': { bgcolor: alpha(accent, 0.08), color: accent },
            }}
          >
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 1.5, md: 3 } }} dividers={false}>
        {/* MUI zeroes the content padding after a DialogTitle, so space the form itself. */}
        <Box sx={{ pt: { xs: 2, md: 2.5 } }}>
          <InventoryLeadForm
            key={mountKey}
            selectedLead={selectedLead}
            initialContactId={selectedLead ? null : initialContactId}
            initialCompanyId={selectedLead ? null : initialCompanyId}
            onCreated={handleCreated}
            onUpdated={handleUpdated}
            onCancel={onClose}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default InventoryLeadFormDialog;
